/**
 * IncomingCallScreen — shown when an inbound call is ringing.
 *
 * Displays the caller's phone number and, if available, their contact name
 * and tags. Plays ring.mp3 from the public folder on loop. The rep can
 * answer or decline the call.
 *
 * Answering  → calls client.answer() via the atClient hook.
 * Declining  → calls client.hangup(), then POSTs /calls/:sessionId/decline for audit.
 *              AT handles waterfall continuation automatically.
 */

import { Phone, PhoneOff, User } from "lucide-react";
import type { IncomingCallInfo } from "../hooks/useATClient";

// ─── Component ────────────────────────────────────────────────────────────────

interface IncomingCallScreenProps {
  incomingCall: IncomingCallInfo;
  onAnswer: () => void;
  onDecline: () => void;
}

export function IncomingCallScreen({ incomingCall, onAnswer, onDecline }: IncomingCallScreenProps) {
  // Ringtone is managed by App.tsx (preloaded there to avoid autoplay blocking).

  const handleAnswer = () => {
    onAnswer();
  };

  const handleDecline = () => {
    onDecline();
  };

  const { callerNumber, contactName, contactTags } = incomingCall;

  // Format a raw E.164 number into a readable display (e.g. +234 801 234 5678).
  const displayNumber = callerNumber.startsWith("+")
    ? callerNumber.replace(/(\+\d{1,3})(\d{3})(\d{3})(\d+)/, "$1 $2 $3 $4")
    : callerNumber;

  return (
    <div className="flex h-full flex-col items-center justify-between bg-background px-6 py-8">
      {/* ── Top: incoming badge ─────────────────────────────────────────────── */}
      <div className="flex w-full justify-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground">
          <Phone className="h-3.5 w-3.5" />
          Incoming
        </span>
      </div>

      {/* ── Middle: caller info ─────────────────────────────────────────────── */}
      <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
        {/* Avatar / unknown indicator */}
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted">
          <User className="h-10 w-10 text-muted-foreground" />
        </div>

        {/* Contact name (if known) */}
        {contactName && <p className="text-2xl font-bold text-foreground">{contactName}</p>}

        {/* Phone number */}
        <p
          className={
            contactName ? "text-base text-muted-foreground" : "text-2xl font-bold text-foreground"
          }
        >
          {displayNumber}
        </p>

        {/* Tags */}
        {contactTags.length > 0 && (
          <div className="flex flex-wrap justify-center gap-1.5">
            {contactTags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Bottom: answer / decline buttons ────────────────────────────────── */}
      <div className="flex w-full items-center justify-around pb-4">
        {/* Decline */}
        <button
          type="button"
          onClick={handleDecline}
          aria-label="Decline call"
          className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive shadow-lg transition-transform active:scale-95"
        >
          <PhoneOff className="h-7 w-7 text-destructive-foreground" />
        </button>

        {/* Answer */}
        <button
          type="button"
          onClick={handleAnswer}
          aria-label="Answer call"
          className="flex h-16 w-16 items-center justify-center rounded-full bg-primary shadow-lg transition-transform active:scale-95"
        >
          <Phone className="h-7 w-7 text-primary-foreground" />
        </button>
      </div>
    </div>
  );
}
